import React, { useState } from "react";
import { Camera, Upload, FileText, Loader2, CheckCircle2, History } from "lucide-react";
import { api, STATUSES } from "@/lib/apiClient";
import { toast } from "sonner";

const stamp = (iso) =>
  iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "";

export const KategoriRows = ({ jo, role, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const canUpload = role !== "produksi" && role !== "qc";
  const items = jo.items || [];

  const setStatus = async (kategori, status) => {
    setBusy(kategori);
    try {
      const { data } = await api.put(
        `/job-orders/${jo.id}/items/${encodeURIComponent(kategori)}`,
        { status }
      );
      toast.success(`${kategori} → ${status}`);
      const n = data?.notification;
      if (n) {
        if (n.sent) toast.success("Notifikasi WhatsApp terkirim ke client");
        else toast.warning("Semua kategori selesai — kirim WA manual lewat tombol hijau");
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal update status kategori");
    } finally {
      setBusy(null);
    }
  };

  const uploadPhoto = async (kategori, file) => {
    if (!file) return;
    setBusy(kategori);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/job-orders/${jo.id}/items/${encodeURIComponent(kategori)}/photo`, fd);
      toast.success(`Foto kategori ${kategori} terunggah`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah foto");
    } finally {
      setBusy(null);
    }
  };

  const viewPhoto = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Gagal membuka foto");
    }
  };

  const allDone = items.length > 0 && items.every((i) => ["SELESAI", "DITERIMA"].includes(i.status));

  return (
    <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4" data-testid={`kategori-rows-${jo.jo_number}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
          Status & Foto per Kategori
        </span>
        {allDone && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300">
            <CheckCircle2 className="w-3.5 h-3.5" /> Semua kategori tuntas
          </span>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.kategori}
            data-testid={`kategori-item-${jo.jo_number}-${it.kategori}`}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-white/5 bg-slate-900/70 px-3 py-2.5"
          >
            <span className="min-w-[92px] rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200 text-center">
              {it.kategori}
            </span>
            <select
              data-testid={`kategori-status-${jo.jo_number}-${it.kategori}`}
              value={it.status}
              disabled={busy === it.kategori}
              onChange={(e) => setStatus(it.kategori, e.target.value)}
              className="h-9 rounded-lg bg-slate-950/80 border border-white/10 px-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {it.photo_file_id ? (
              <button
                data-testid={`kategori-view-photo-${jo.jo_number}-${it.kategori}`}
                onClick={() => viewPhoto(it.photo_file_id)}
                className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors duration-200"
              >
                <FileText className="w-4 h-4" /> Lihat foto
              </button>
            ) : (
              <span className="text-xs text-slate-600">Belum ada foto</span>
            )}

            {canUpload && (
              <div className="flex items-center gap-3 ml-auto">
                <label
                  data-testid={`kategori-upload-${jo.jo_number}-${it.kategori}`}
                  className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 cursor-pointer transition-colors duration-200"
                >
                  {busy === it.kategori ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {it.photo_file_id ? "Ganti" : "Upload"}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => uploadPhoto(it.kategori, e.target.files?.[0])}
                  />
                </label>
                <label
                  data-testid={`kategori-camera-${jo.jo_number}-${it.kategori}`}
                  className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 cursor-pointer transition-colors duration-200"
                >
                  <Camera className="w-4 h-4" /> Kamera
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => uploadPhoto(it.kategori, e.target.files?.[0])}
                  />
                </label>
              </div>
            )}
            <div
              className="w-full mt-1 border-t border-white/5 pt-2 space-y-1"
              data-testid={`kategori-history-${jo.jo_number}-${it.kategori}`}
            >
              {(jo.item_logs || []).filter((l) => l.kategori === it.kategori).length === 0 ? (
                <div className="text-[10px] text-slate-600">Belum ada riwayat kategori ini</div>
              ) : (
                (jo.item_logs || [])
                  .filter((l) => l.kategori === it.kategori)
                  .slice(-5)
                  .reverse()
                  .map((l) => (
                    <div key={`kl-${l.at}-${l.status}`} className="flex items-center gap-2 text-[10px] text-slate-400">
                      <History className="w-3 h-3 text-sky-300 shrink-0" />
                      <span className="font-semibold text-slate-200">{l.status}</span>
                      oleh <span className="font-semibold text-sky-200">{l.by}</span>
                      <span className="text-slate-500">{stamp(l.at)}</span>
                    </div>
                  ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
